export const TERMS_EMAIL = 'info@storyfy.ge'
export const TERMS_WEBSITE = 'https://storyfy.ge'

// ---------------------------------------------------------------------------
// Commercial terms quoted in the documents below. These MUST stay in sync with
// the backend configuration they describe (backend/.env.example):
//   TERMS_FEE            -> STORYFY_COMMISSION_RATE (0.05)
//   TERMS_REFERRAL_FEE   -> STORYFY_REFERRAL_RATE   (0.001)
//   AI_FREE_TRIES        -> AI_DESIGN_FREE_TRIES    (3)
//   AI_PRICE_GEL         -> AI_DESIGN_PRICE_GEL     (2)
//   MEDIA_PRICE_PER_GB   -> MEDIA_PRICE_PER_GB_GEL  (0.3)
//   MEDIA_SERVICE_DAYS   -> MEDIA_SERVICE_DAYS      (60)
//   MEDIA_GRACE_DAYS     -> MEDIA_GRACE_DAYS        (30)
// Changing a price in .env without changing it here publishes a false offer.
//
// ADDON_REFUND_DAYS has no backend counterpart — it is pure policy, enforced by
// whoever handles the refund request, not by code. It lives here rather than in
// refundContent because both documents quote it and they must never disagree.
// ---------------------------------------------------------------------------

/** Storyfy's commission on each gift raised through a wishlist. */
export const TERMS_FEE = '5%'
/** Referrer's share of each gift — paid OUT OF TERMS_FEE, never out of the host's net. */
export const TERMS_REFERRAL_FEE = '0.1%'
/** Free AI invitation generations per account before pay-per-generation starts. */
export const AI_FREE_TRIES = 3
/** Price per AI generation once the free tries are used up, in GEL. */
export const AI_PRICE_GEL = 2
/** Flat album storage price per gigabyte, in GEL — same rate for top-ups. */
export const MEDIA_PRICE_PER_GB = 0.3
/** Days the album stays open for uploads, counted from the first storage purchase. */
export const MEDIA_SERVICE_DAYS = 60
/** Days the album stays readable after the window closes, before permanent deletion. */
export const MEDIA_GRACE_DAYS = 30
/** Days a buyer has to ask for a refund of an UNUSED add-on. Quoted by both the
 *  terms (clause 11.4) and the refund policy, so it must live in one place. */
export const ADDON_REFUND_DAYS = 7

const MEDIA_PRICE_LABEL = MEDIA_PRICE_PER_GB.toFixed(2)

/** ISO date (YYYY-MM-DD). Update whenever you change the terms below. */
export const TERMS_LAST_UPDATED = '2026-09-03'

export const termsContent = {
  en: {
    title: 'Terms and Conditions of Service (Public Offer)',
    lastUpdatedLabel: 'Last updated:',
    intro: [
      'This document constitutes a legally binding agreement (public offer) governing the relationship between the online platform Storyfy.ge (hereinafter referred to as the “Company,” “Platform,” or “Administration”) and its users (event organizers, celebrants, and guests).',
      'Registration on the website, use of any service, or completing any transaction through the platform’s payment system means that you fully, unconditionally, and knowingly agree to these Terms and Conditions. If you do not agree with any provision, please immediately stop using the platform.',
      'Creating and running an event on Storyfy.ge is free. The Company earns a commission on the gifts an event raises, and separately sells two optional paid add-ons (the AI invitation designer and event album storage). There are no subscriptions and no up-front packages.',
    ],
    closing:
      'This document serves as the complete legal agreement between the platform and its users.',
    footer: {
      poweredBy: 'Powered by Storyfy.ge',
      copyright: '© 2026 Storyfy.ge. All rights reserved.',
      contact: 'Contact:',
      termsLink: 'Terms of Service',
      privacyLink: 'Privacy Policy',
    },
    sections: [
      {
        title: '1. Definitions',
        list: [
          {
            label: 'Platform / Company',
            text: 'the online resource Storyfy.ge, operated by a sole proprietor registered under the laws of Georgia.',
          },
          {
            label: 'Event Organizer (Host / Celebrant / Beneficiary)',
            text: 'an adult, legally capable individual or legal entity who registers on the website, creates a virtual invitation card, a guest list, and a gift list (Wishlist).',
          },
          {
            label: 'Guest (User)',
            text: 'any person who visits the organizer’s shared link, responds to the invitation, transfers a monetary gift to the organizer through the platform’s payment channels, or uploads photos and videos to the event album.',
          },
          {
            label: 'Wishlist',
            text: 'a visual catalog/list of desired gifts created by the organizer on the platform.',
          },
          {
            label: 'Event Wallet',
            text: 'the running record of what a specific event has raised — the cleared gifts it received, minus the commission already withheld — held separately for each currency.',
          },
          {
            label: 'Account Balance',
            text: 'the organizer’s own balance on the platform, kept as an append-only ledger and denominated separately in each currency. Funds move here from the Event Wallet when an event is closed, and referral bonuses are credited here.',
          },
          {
            label: 'Event Album',
            text: 'the paid photo and video storage attached to an event, into which the organizer and (if enabled) their guests upload files.',
          },
          {
            label: 'AI Invitation Designer',
            text: 'the paid tool that generates a background image, font pairing, and colour palette for an invitation from the details the organizer supplies.',
          },
          {
            label: 'Invitation Template',
            text: 'one of the designed looks in the platform’s template library — paper, type, artwork and layout — that the organizer applies to their invitation. Templates, and the colour, background and font adjustments made to them, are free.',
          },
          {
            label: 'Design Library',
            text: 'the collection of Invitation Templates, motifs and font pairings the Company publishes on the platform and in its public gallery.',
          },
          {
            label: 'Referral Programme',
            text: 'the scheme under which a registered user who introduces a new organizer to the platform receives a share of the Company’s commission on that organizer’s gifts.',
          },
          {
            label: 'Add-on Services',
            text: 'the optional paid services the Company sells directly to organizers — currently AI generation credits and Event Album storage.',
          },
        ],
      },
      {
        title: '2. Legal Status of the Platform and Subject of Agreement',
        list: [
          {
            label: '2.1.',
            text: 'In relation to gifts, Storyfy.ge acts exclusively as a technological, informational, and organizational intermediary (agent) between the event organizer and the guest.',
          },
          {
            label: '2.2.',
            text: 'The platform itself is not the seller, supplier, or owner of any physical or digital goods, vouchers, or services displayed in the Wishlist. Items shown in the Wishlist are visual ideas/indicators only, allowing the guest to choose the amount of money they wish to gift to the organizer.',
            emphasize: ['is not'],
          },
          {
            label: '2.3.',
            text: 'Any payment made by a guest is considered a targeted monetary gift (gift transfer under the Civil Code of Georgia) made directly to the organizer (celebrant). The platform only technically facilitates the temporary receipt, processing, and transfer of such funds to the beneficiary.',
          },
          {
            label: '2.4.',
            text: 'In relation to Add-on Services, the Company acts as the direct provider and seller, not as an intermediary. A purchase of AI generation credits or Event Album storage is a separate paid contract between the organizer and the Company, governed by Sections 5–7 of this document and by the Refund Policy.',
            emphasize: ['direct provider and seller, not as an intermediary'],
          },
        ],
      },
      {
        title: '3. Registration, Account, and Access',
        list: [
          {
            label: '3.1.',
            text: 'An account can be created with an email address and a password, or by signing in with Google. When Google sign-in is used, the Company receives only the basic profile data Google returns (name, email address, profile picture); the Company never receives the user’s Google password.',
          },
          {
            label: '3.2.',
            text: 'Registration is available only to adult, legally capable persons. The user undertakes to provide accurate information and to keep it up to date.',
          },
          {
            label: '3.3.',
            text: 'The user is responsible for the security of their account credentials. The Company will never ask for a password by phone, email, or message, and no employee of the Company has access to a user’s password.',
            emphasize: ['never ask for a password'],
          },
          {
            label: '3.4.',
            text: 'The platform does not send automated password-reset links by email. A “forgot password” request submitted from the login page is placed in a support queue; once account ownership is verified, the Company sets a temporary password and communicates it through a separate channel. The user must change that temporary password immediately after signing in.',
          },
          {
            label: '3.5.',
            text: 'A signed-in session is maintained by a technical session identifier stored in the browser. Signing out ends the session. The Company may end sessions for security reasons.',
          },
        ],
      },
      {
        title: '4. Financial Terms, Fees, and Tax Obligations',
        list: [
          {
            label: '4.1.',
            text: 'Creating an event, sending invitations, collecting RSVPs, and publishing a Wishlist are free of charge. There is no subscription fee and no up-front package: an event is live from the moment it is created.',
            emphasize: ['free of charge'],
          },
          {
            label: '4.2.',
            text: `For the platform’s technical and software services, an intermediary service fee of ${TERMS_FEE} is charged on each gift raised through the Wishlist. The fee is withheld at the moment the gift is credited to the Event Wallet; the guest pays exactly the amount they chose, and the organizer’s net is the gift minus the fee.`,
          },
          {
            label: '4.3.',
            text: 'Guests may pay by bank card through the hosted checkout page of a licensed payment provider. Card details are entered on the provider’s own page — the Company neither receives nor stores full card data. Where the Company offers direct bank transfer for its own Add-on Services, that method carries no card-processing fee.',
          },
          {
            label: '4.4.',
            text: 'Gifts may be priced in GEL, USD, or EUR. Money is received, recorded, and paid out in the same currency in which it was given. The Company does not convert between currencies and does not sum balances across currencies; any conversion is a matter between the user and their own bank.',
            emphasize: ['does not convert between currencies'],
          },
          {
            label: '4.5.',
            text: 'Under the Tax Code of Georgia, the Company’s taxable revenue consists only of the service fee defined in Section 4.2 and the fees charged for Add-on Services under Section 5.',
            emphasize: ['only of the service fee defined in Section 4.2'],
          },
          {
            label: '4.6.',
            text: 'The principal gift amount transferred by guests through the website (the full balance minus the platform fee) does not constitute income or property of the platform. Therefore, this amount is not taxable on the Company’s side.',
          },
          {
            label: '4.7.',
            text: 'The organizer (celebrant) is solely responsible for any tax obligations (if applicable) that may arise from gifts/transfers received from individuals under applicable law.',
          },
          {
            label: '4.8.',
            text: 'While an event is open, the funds it has raised are shown in its Event Wallet. When the organizer closes the event, the net amount per currency is transferred to their Account Balance and recorded as a ledger entry naming that event.',
          },
          {
            label: '4.9.',
            text: 'Transfer of the Account Balance to the organizer’s personal bank account is processed within no more than 3 (three) business days after the organizer submits a payout request. Payouts are made only to a bank account held in the name of the account holder.',
            emphasize: ['no more than 3 (three) business days'],
          },
          {
            label: '4.10.',
            text: 'The Account Balance is a record of amounts the Company owes the user. It is not a bank deposit, not electronic money, and not an investment; it bears no interest.',
          },
          {
            label: '4.11.',
            text: 'The Company may delay a payout for as long as reasonably necessary to verify the account holder’s identity, to investigate suspected fraud, or to resolve a chargeback or a dispute concerning the underlying gifts.',
          },
        ],
      },
      {
        title: '5. Add-on Services and Prices',
        intro:
          'The following optional services are sold by the Company directly. They are extras on top of a free event, never a condition of using the platform. The Design Library — every invitation template, motif, colour, background and font pairing, and the live preview — is free; the only paid add-ons are the two listed here.',
        list: [
          {
            label: '5.1. AI generation credits:',
            text: `each account receives ${AI_FREE_TRIES} free invitation generations. After those are used, further generations are purchased as credits at ${AI_PRICE_GEL} GEL per generation. A generation that fails for technical reasons consumes neither a free try nor a credit. Credits are tied to the account, are not transferable, have no cash value, and do not expire while the account is active.`,
          },
          {
            label: '5.2. Event Album storage:',
            text: `storage is sold per gigabyte at a flat rate of ${MEDIA_PRICE_LABEL} GEL per GB — the same rate for the first purchase and for every later top-up. The first purchase for an event opens a service window of ${MEDIA_SERVICE_DAYS} days. Top-ups add capacity to the album; they do not extend the window.`,
          },
          {
            label: '5.3. End of the storage window:',
            text: `when the ${MEDIA_SERVICE_DAYS}-day window ends, the album is locked — no new uploads — but remains viewable and downloadable for a grace period of ${MEDIA_GRACE_DAYS} days. After the grace period, all files in the album are permanently deleted from storage. The Company keeps no other copy, so the organizer must download anything they wish to keep before that point.`,
            emphasize: ['permanently deleted'],
          },
          {
            label: '5.4.',
            text: 'Add-on Services are optional. Declining to buy them does not restrict any of the free features of an event.',
          },
          {
            label: '5.5.',
            text: 'The Company may change the price of an Add-on Service at any time, with effect for future purchases only. A price change never affects an add-on already paid for.',
          },
          {
            label: '5.6.',
            text: 'Refund rules for Add-on Services differ from those for gifts and are set out in Section 11 and in the Refund Policy published on the website.',
          },
        ],
      },
      {
        title: '6. The Event Album, Guest Uploads, and Content Rules',
        list: [
          {
            label: '6.1.',
            text: 'If the organizer enables guest uploads, anyone holding the event’s share link may upload photos and videos without registering. Files are transferred from the guest’s browser directly to the Company’s storage provider using short-lived, single-purpose upload links; the files do not pass through the website’s own servers.',
          },
          {
            label: '6.2.',
            text: 'The organizer controls the upload switch and may at any time turn guest uploads off, sort and move files between folders, delete individual files, or download the entire album.',
          },
          {
            label: '6.3.',
            text: 'Technical limits apply: up to 25 MB per photo and 4 GB per video, and up to 500 files and 50 GB in total per event. Rate limits also apply to the open upload link to protect it from abuse.',
          },
          {
            label: '6.4.',
            text: 'The organizer is responsible for the album: for choosing to open an upload link, for whom they share that link with, and for the content that ends up in it. The Company does not pre-moderate uploads.',
            emphasize: ['does not pre-moderate'],
          },
          {
            label: '6.5.',
            text: 'It is prohibited to upload content that is unlawful, that depicts a person in a private or intimate setting without their consent, that sexualises or endangers a minor, or that infringes the rights of third parties. Any person shown in an uploaded file may request its removal by writing to info@storyfy.ge.',
          },
          {
            label: '6.6.',
            text: 'Deleting an event permanently deletes its album — both the files and their records. This action cannot be undone and the Company retains no backup. The organizer is warned of this before confirming deletion.',
            emphasize: ['cannot be undone'],
          },
        ],
      },
      {
        title: '7. AI Invitation Designer',
        list: [
          {
            label: '7.1.',
            text: 'The designer produces a background image, a font pairing, and a colour palette based on the details the organizer supplies — the event title, event type, language, and an optional free-text description. Those details are transmitted to a third-party artificial-intelligence provider for processing. Organizers should not include information they do not wish to have processed by a third party.',
            emphasize: ['third-party artificial-intelligence provider'],
          },
          {
            label: '7.2.',
            text: 'AI output is generated automatically and is provided “as is”. The Company does not guarantee that a result will be unique, accurate, or suitable for any particular purpose, and does not guarantee that a similar result will not be produced for another user.',
          },
          {
            label: '7.3.',
            text: 'If the external AI provider is unavailable, the platform produces a design with its own built-in generator instead. Such a design counts as a delivered generation.',
          },
          {
            label: '7.4.',
            text: 'The organizer may use the generated design for their event on the platform and in the invitations shared from it. The Company retains a record of each generation for accounting and abuse-prevention purposes.',
          },
        ],
      },
      {
        title: '8. Referral Programme',
        list: [
          {
            label: '8.1.',
            text: `Every registered user has a personal referral code and link. When a new organizer registers through that link and their event receives gifts, the referrer earns ${TERMS_REFERRAL_FEE} of each such gift.`,
          },
          {
            label: '8.2.',
            text: `The referral bonus is paid out of the Company’s ${TERMS_FEE} commission — never out of the organizer’s money. A referred organizer receives exactly the same net amount as one who was not referred.`,
            emphasize: ['never out of the organizer’s money'],
          },
          {
            label: '8.3.',
            text: 'Bonuses are credited to the referrer’s Account Balance in the currency of the underlying gift and are paid out under the same rules as any other balance (Section 4).',
          },
          {
            label: '8.4.',
            text: 'The following are prohibited: referring oneself, registering additional accounts in order to generate bonuses, creating events or funding gifts with one’s own payment instrument in order to farm bonuses, and distributing referral links by spam or misrepresentation. Bonuses obtained in these ways are forfeited, and the account may be blocked.',
          },
          {
            label: '8.5.',
            text: 'The Company may change or discontinue the referral rate at any time, with effect for the future. Bonuses already credited are not affected.',
          },
        ],
      },
      {
        title: '9. Limitation of Liability and Warranties',
        list: [
          {
            label: '9.1.',
            text: 'The Company is not responsible for how monetary gifts are used by the celebrant after payout. The platform does not monitor or guarantee that the organizer will actually purchase the items displayed in the Wishlist.',
          },
          {
            label: '9.2.',
            text: 'The Company is not liable for the content or quality of any event registered on the platform, nor for cancellation, postponement, or any organizational issue related to such event.',
          },
          {
            label: '9.3.',
            text: 'The Administration is not responsible for technical failures, delays, or rejected transactions caused by banks, payment processors, internet service providers, or acquiring banks.',
          },
          {
            label: '9.4.',
            text: 'The user (organizer) is responsible for maintaining the security of their profile (login credentials and password). The Company bears no liability for damages resulting from unauthorized third-party access.',
          },
          {
            label: '9.5.',
            text: 'The platform is provided on an “as is” and “as available” basis. The Company does not warrant uninterrupted or error-free operation, and may carry out maintenance that temporarily limits access.',
          },
          {
            label: '9.6.',
            text: 'The Company is not responsible for content uploaded by guests, nor for the loss of album files that follows the end of the storage window, the expiry of the grace period, or the organizer’s own deletion of the event, as described in Sections 5 and 6.',
          },
          {
            label: '9.7.',
            text: 'To the extent permitted by applicable law, the Company’s aggregate liability in connection with an Add-on Service is limited to the amount actually paid by the user for that service during the 6 (six) months preceding the event giving rise to the claim. Nothing in this document limits liability that cannot be limited by law.',
          },
        ],
      },
      {
        title: '10. Suspension and Termination',
        list: [
          {
            label: '10.1.',
            text: 'The user may stop using the platform at any time and may request deletion of their account by writing to the contact address in Section 16.',
          },
          {
            label: '10.2.',
            text: 'The Company may suspend or terminate an account, block an event, or withhold a payout where there is a well-founded suspicion of a breach of these Terms, of fraud, or of a violation of applicable law.',
          },
          {
            label: '10.3.',
            text: 'Termination does not extinguish an Account Balance lawfully accrued. Any undisputed balance remains payable to the user, subject to the verification described in Section 4.11.',
          },
          {
            label: '10.4.',
            text: 'Closing an account ends any active storage window; album files are removed in accordance with Section 5.3, and unused AI credits lapse.',
          },
        ],
      },
      {
        title: '11. Refund Policy',
        intro:
          'Gifts and Add-on Services follow different refund rules. The full text is published as a separate Refund Policy on the website; the summary is as follows.',
        list: [
          {
            label: '11.1. Gifts:',
            text: 'a guest may request a refund only if the event has been officially canceled by the organizer and the accumulated funds have not yet been transferred to the organizer’s personal bank account.',
            emphasize: ['only'],
          },
          {
            label: '11.2.',
            text: 'Once funds are transferred to the organizer’s bank account, the platform bears no further responsibility for any financial claims. Any dispute regarding refunds between the guest and the organizer must be resolved directly between them, outside the platform.',
          },
          {
            label: '11.3.',
            text: 'In the event of a refund, banking or payment processing fees (if applicable) may be deducted from the refunded amount. Where a gift is refunded, the commission withheld on that gift is refunded with it.',
          },
          {
            label: '11.4. Add-on Services:',
            text: `unused AI generation credits and unused album storage may be refunded within ${ADDON_REFUND_DAYS} days of purchase. AI generations that have already been delivered, and album storage under which files have already been uploaded, are not refundable, because the service has been performed. This does not affect rights that cannot be waived under consumer protection law.`,
          },
        ],
      },
      {
        title: '12. Intellectual Property and Copyright',
        list: [
          {
            label: '12.1. Platform Ownership:',
            text: 'All materials published on Storyfy.ge, including but not limited to software code, databases, website design, interface, graphic elements, animations, trademarks/logo, texts, and audiovisual content, are the exclusive intellectual property of the Company. Any copying, reproduction, distribution, modification, or commercial use without prior written consent is strictly prohibited and punishable by law.',
          },
          {
            label: '12.2. User Uploaded Content:',
            text: 'The organizer may upload photos, videos, music, or text to personalize invitations, Wishlists, and the Event Album. The user represents and warrants that they are the lawful owner of such content or have obtained all required licenses or permissions. The user retains ownership of their content and grants the Company only the limited licence needed to store, process, and display it for the operation of the service.',
          },
          {
            label: '12.3. AI-Generated Designs:',
            text: 'Designs produced by the AI Invitation Designer may be used by the organizer for their event and its invitations. Because such output is generated automatically, the Company makes no representation that it is protectable by copyright or that it is exclusive to the organizer.',
          },
          {
            label: '12.4. Third-Party Rights Disclaimer:',
            text: 'The Company does not proactively moderate uploaded content for copyright compliance. In the event of infringement of third-party intellectual property rights, full legal and financial responsibility lies with the user who uploaded the content.',
          },
          {
            label: '12.5. Copyright Infringement Notice (Take-Down Notice):',
            text: 'If a rights holder believes content published on Storyfy.ge infringes their copyright, they may request removal by sending notice to info@storyfy.ge. Upon reasonable evidence, the Company will promptly remove or block the disputed material.',
          },
          {
            label: '12.6. Invitation Templates and the Design Library:',
            text: 'The templates, motifs, layouts and other artwork in the Design Library are the property of the Company or its licensors. The organizer receives a non-exclusive, non-transferable licence to use a template for the invitation of their own event on the platform, for as long as that invitation exists. Extracting a template’s artwork or design, reproducing it outside the platform, reselling it, or presenting it as one’s own is not permitted. Typefaces are served from Google Fonts under their open-source licences; stock cover photographs are provided by Unsplash under the Unsplash Licence. A photograph the organizer uploads as a cover remains the organizer’s under Section 12.2.',
          },
        ],
      },
      {
        title: '13. Prohibited Activities',
        intro:
          'Use of the platform is strictly prohibited for events or purposes that:',
        list: [
          { text: 'violate the laws of Georgia;' },
          { text: 'involve money laundering, fraud, or other criminal schemes;' },
          {
            text: 'aim to finance illegal substances, weapons, or prohibited services;',
          },
          {
            text: 'infringe third-party copyright or intellectual property rights;',
          },
          {
            text: 'abuse the open guest-upload link, or use the Event Album to distribute unlawful material or images of a person published without their consent;',
          },
          {
            text: 'abuse the AI Invitation Designer, including attempts to generate unlawful, hateful, or sexual imagery, or to bypass the generation limits;',
          },
          {
            text: 'manipulate the Referral Programme, including self-referral, duplicate accounts, and self-funded gifts;',
          },
          {
            text: 'arrange payment outside the platform in order to avoid the commission on gifts collected through a Storyfy.ge event, or attempt to interfere with, probe, or overload the platform’s technical infrastructure.',
          },
        ],
        outro:
          'The Company reserves the right, in case of suspicion, to unilaterally block any event or account, suspend payout of funds, and provide relevant information to law enforcement authorities.',
      },
      {
        title: '14. Force Majeure',
        paragraph:
          'The parties shall not be liable for failure to perform obligations due to force majeure events, including but not limited to natural disasters, war, military actions, government regulations, large-scale cyberattacks, or global internet outages.',
      },
      {
        title: '15. Changes to Terms and Conditions',
        list: [
          {
            label: '15.1.',
            text: 'Storyfy.ge reserves the right to amend this document unilaterally at any time. The updated version becomes effective immediately upon publication on the website.',
          },
          {
            label: '15.2.',
            text: 'Users are responsible for periodically reviewing the Terms. Continued use of the platform after updates are published constitutes automatic acceptance of the changes.',
          },
          {
            label: '15.3.',
            text: 'A change to the commission, to the referral rate, or to the price of an Add-on Service takes effect for the future only. Gifts already received, bonuses already credited, and add-ons already paid for are governed by the terms in force at the time.',
          },
        ],
      },
      {
        title: '16. Dispute Resolution and Contact Information',
        list: [
          {
            label: '16.1.',
            text: 'Any dispute arising from this agreement shall first be resolved through negotiation. If no agreement is reached, the dispute shall be handled by the courts of Georgia in accordance with Georgian law.',
          },
        ],
        contact: {
          title: '16.2. Contact Information:',
          emailLabel: 'Email:',
          websiteLabel: 'Website:',
        },
      },
    ],
  },
  ka: {
    title: 'მომსახურების წესები და პირობები (საჯარო ოფერტი)',
    lastUpdatedLabel: 'ბოლო განახლების თარიღი:',
    intro: [
      'წინამდებარე დოკუმენტი წარმოადგენს იურიდიულად სავალდებულო ხელშეკრულებას (საჯარო ოფერტს), რომელიც არეგულირებს ურთიერთობას ონლაინ პლატფორმა Storyfy.ge-სა (შემდგომში „კომპანია“, „პლატფორმა“ ან „ადმინისტრაცია“) და მომხმარებლებს (ღონისძიების ორგანიზატორებს, იუბილარებსა და სტუმრებს) შორის.',
      'ვებ-გვერდზე რეგისტრაცია, ნებისმიერი სერვისით სარგებლობა ან პლატფორმის საგადახდო სისტემის საშუალებით ტრანზაქციის განხორციელება ნიშნავს, რომ თქვენ სრულად, უპირობოდ და გაცნობიერებულად ეთანხმებით წინამდებარე წესებსა და პირობებს. თუ არ ეთანხმებით რომელიმე პუნქტს, გთხოვთ, დაუყოვნებლივ შეწყვიტოთ პლატფორმით სარგებლობა.',
      'Storyfy.ge-ზე ღონისძიების შექმნა და წარმართვა უფასოა. კომპანიის შემოსავალს წარმოადგენს ღონისძიების მიერ მოზიდულ სასაჩუქრე თანხაზე დაწესებული საკომისიო, ასევე ორი დამატებითი ფასიანი სერვისი (ხელოვნური ინტელექტის დიზაინერი და ღონისძიების ალბომის სივრცე). პლატფორმაზე არ არსებობს არც აბონენტური გადასახადი და არც წინასწარი პაკეტები.',
    ],
    closing: null,
    footer: {
      poweredBy: 'Storyfy.ge-ით',
      copyright: '© 2026 Storyfy.ge. ყველა უფლება დაცულია.',
      contact: 'საკონტაქტო:',
      termsLink: 'მომსახურების წესები და პირობები',
      privacyLink: 'კონფიდენციალურობის პოლიტიკა',
    },
    sections: [
      {
        title: '1. ტერმინთა განმარტება',
        list: [
          {
            label: 'პლატფორმა / კომპანია',
            text: 'ინტერნეტ-რესურსი Storyfy.ge, რომელიც ფუნქციონირებს საქართველოს კანონმდებლობის შესაბამისად რეგისტრირებული ინდივიდუალური მეწარმის მიერ.',
          },
          {
            label: 'ღონისძიების ორგანიზატორი (მასპინძელი / იუბილარი / ბენეფიციარი)',
            text: 'სრულწლოვანი, ქმედუნარიანი ფიზიკური ან იურიდიული პირი, რომელიც რეგისტრირდება საიტზე, ქმნის ვირტუალურ საინვაიტო ბარათს, სტუმრების სიას და სასაჩუქრე სიას (Wishlist).',
          },
          {
            label: 'სტუმარი (მომხმარებელი)',
            text: 'ნებისმიერი პირი, რომელიც სტუმრობს ორგანიზატორის მიერ შექმნილ ბმულს, პასუხობს მოწვევას, პლატფორმის საგადახდო არხების გამოყენებით რიცხავს სასაჩუქრე თანხას ორგანიზატორის სასარგებლოდ ან ტვირთავს ფოტო-ვიდეო მასალას ღონისძიების ალბომში.',
          },
          {
            label: 'ვიშლისტი (Wishlist)',
            text: 'ორგანიზატორის მიერ პლატფორმაზე შექმნილი სასურველი საჩუქრების ვიზუალური კატალოგი/სია.',
          },
          {
            label: 'ღონისძიების საფულე',
            text: 'კონკრეტული ღონისძიების მიერ მოზიდული თანხის მიმდინარე აღრიცხვა — დადასტურებული სასაჩუქრე ჩარიცხვები დაკავებული საკომისიოს გამოკლებით. აღრიცხვა ცალ-ცალკე იწარმოება თითოეული ვალუტისთვის.',
          },
          {
            label: 'ანგარიშის ბალანსი',
            text: 'ორგანიზატორის პირადი ბალანსი პლატფორმაზე, რომელიც იწარმოება მხოლოდ დამატებადი ჩანაწერების სახით და ცალ-ცალკე აღირიცხება თითოეულ ვალუტაში. აქ გადმოდის თანხა ღონისძიების საფულიდან მისი დახურვისას, ასევე აქ ირიცხება სარეფერალო ბონუსები.',
          },
          {
            label: 'ღონისძიების ალბომი',
            text: 'ღონისძიებაზე მიბმული ფასიანი ფოტო- და ვიდეო-სივრცე, რომელშიც ორგანიზატორი და (ჩართვის შემთხვევაში) მისი სტუმრები ტვირთავენ ფაილებს.',
          },
          {
            label: 'ხელოვნური ინტელექტის დიზაინერი',
            text: 'ფასიანი ინსტრუმენტი, რომელიც ორგანიზატორის მიერ მითითებული მონაცემების საფუძველზე ქმნის მოწვევის ფონურ გამოსახულებას, შრიფტების წყვილსა და ფერთა პალიტრას.',
          },
          {
            label: 'მოწვევის შაბლონი',
            text: 'პლატფორმის შაბლონების ბიბლიოთეკაში არსებული ერთ-ერთი დიზაინი — ქაღალდი, შრიფტი, გრაფიკა და განლაგება — რომელსაც ორგანიზატორი საკუთარ მოწვევას არგებს. შაბლონები და მათზე შესრულებული ფერის, ფონისა და შრიფტის ცვლილებები უფასოა.',
          },
          {
            label: 'დიზაინის ბიბლიოთეკა',
            text: 'მოწვევის შაბლონების, მოტივებისა და შრიფტების წყვილების ერთობლიობა, რომელსაც კომპანია აქვეყნებს პლატფორმაზე და მის საჯარო გალერეაში.',
          },
          {
            label: 'სარეფერალო პროგრამა',
            text: 'სქემა, რომლის ფარგლებშიც რეგისტრირებული მომხმარებელი, რომელიც პლატფორმაზე მოიყვანს ახალ ორგანიზატორს, იღებს კომპანიის საკომისიოს ნაწილს ამ ორგანიზატორის მიერ მიღებული საჩუქრებიდან.',
          },
          {
            label: 'დამატებითი სერვისები',
            text: 'არასავალდებულო ფასიანი სერვისები, რომლებსაც კომპანია უშუალოდ სთავაზობს ორგანიზატორებს — ამჟამად ეს არის ხელოვნური ინტელექტის გენერაციის კრედიტები და ღონისძიების ალბომის სივრცე.',
          },
        ],
      },
      {
        title: '2. პლატფორმის სამართლებრივი სტატუსი და ხელშეკრულების საგანი',
        list: [
          {
            label: '2.1.',
            text: 'სასაჩუქრე თანხებთან მიმართებით Storyfy.ge წარმოადგენს ექსკლუზიურად ტექნოლოგიურ, საინფორმაციო და ორგანიზაციულ შუამავალს (აგენტს) ღონისძიების ორგანიზატორსა და სტუმარს შორის.',
          },
          {
            label: '2.2.',
            text: 'პლატფორმა თავად არ წარმოადგენს ვიშლისტში განთავსებული მატერიალური თუ არამატერიალური ნივთების, ვაუჩერების ან მომსახურებების გამყიდველს, მომწოდებელს ან მესაკუთრეს. ვიშლისტში ასახული პოზიციები არის მხოლოდ ვიზუალური იდეები/ინდიკატორები, რომელთა საშუალებითაც სტუმარი ირჩევს, თუ რა მოცულობის ფულადი საჩუქარი სურს გადასცეს ორგანიზატორს.',
            emphasize: ['არ წარმოადგენს'],
          },
          {
            label: '2.3.',
            text: 'სტუმრის მიერ განხორციელებული ნებისმიერი ფულადი ჩარიცხვა წარმოადგენს მიზნობრივ სასაჩუქრე გზავნილს (ჩუქების აქტს საქართველოს სამოქალაქო კოდექსის შესაბამისად) უშუალოდ ორგანიზატორის (იუბილარის) მიმართ. პლატფორმა მხოლოდ ტექნიკურად უზრუნველყოფს ამ თანხების დროებით მიღებას, დამუშავებას და ბენეფიციარისთვის გადაცემას.',
          },
          {
            label: '2.4.',
            text: 'დამატებით სერვისებთან მიმართებით კომპანია მოქმედებს როგორც უშუალო მიმწოდებელი და გამყიდველი და არა როგორც შუამავალი. ხელოვნური ინტელექტის კრედიტების ან ალბომის სივრცის შეძენა წარმოადგენს ორგანიზატორსა და კომპანიას შორის დადებულ ცალკე ფასიან ხელშეკრულებას, რომელსაც არეგულირებს წინამდებარე დოკუმენტის მე-5–მე-7 თავები და თანხის დაბრუნების პოლიტიკა.',
            emphasize: ['უშუალო მიმწოდებელი და გამყიდველი და არა როგორც შუამავალი'],
          },
        ],
      },
      {
        title: '3. რეგისტრაცია, ანგარიში და წვდომა',
        list: [
          {
            label: '3.1.',
            text: 'ანგარიშის შექმნა შესაძლებელია ელ-ფოსტითა და პაროლით, ან Google-ის ანგარიშით ავტორიზაციით. Google-ით შესვლისას კომპანია იღებს მხოლოდ იმ საბაზისო პროფილის მონაცემებს, რომელსაც Google აბრუნებს (სახელი, ელ-ფოსტა, პროფილის სურათი); მომხმარებლის Google-ის პაროლი კომპანიისთვის არასდროს ხდება ხელმისაწვდომი.',
          },
          {
            label: '3.2.',
            text: 'რეგისტრაცია დაშვებულია მხოლოდ სრულწლოვანი, ქმედუნარიანი პირებისთვის. მომხმარებელი კისრულობს ვალდებულებას, მიუთითოს უტყუარი ინფორმაცია და უზრუნველყოს მისი განახლება.',
          },
          {
            label: '3.3.',
            text: 'მომხმარებელი პასუხისმგებელია საკუთარი ავტორიზაციის მონაცემების უსაფრთხოებაზე. კომპანია არასდროს ითხოვს პაროლს ტელეფონით, ელ-ფოსტით ან შეტყობინებით და კომპანიის არცერთ თანამშრომელს არ აქვს წვდომა მომხმარებლის პაროლზე.',
            emphasize: ['არასდროს ითხოვს პაროლს'],
          },
          {
            label: '3.4.',
            text: 'პლატფორმა არ აგზავნის პაროლის აღდგენის ავტომატურ ბმულებს ელ-ფოსტაზე. ავტორიზაციის გვერდიდან გაგზავნილი „პაროლი დამავიწყდა“ მოთხოვნა ხვდება მხარდაჭერის რიგში; ანგარიშზე უფლების დადასტურების შემდეგ კომპანია აყენებს დროებით პაროლს და გადასცემს მას ცალკე არხით. მომხმარებელი ვალდებულია, შესვლისთანავე შეცვალოს ეს დროებითი პაროლი.',
          },
          {
            label: '3.5.',
            text: 'ავტორიზებული სესია შენარჩუნებულია ბრაუზერში დაცული ტექნიკური სესიის იდენტიფიკატორით. სისტემიდან გამოსვლა წყვეტს სესიას. კომპანია უფლებამოსილია უსაფრთხოების მიზნით შეწყვიტოს სესიები.',
          },
        ],
      },
      {
        title: '4. ფინანსური პირობები, საკომისიო და საგადასახადო ვალდებულებები',
        list: [
          {
            label: '4.1.',
            text: 'ღონისძიების შექმნა, მოწვევების დაგზავნა, დასწრების დადასტურებების (RSVP) მიღება და ვიშლისტის გამოქვეყნება უფასოა. არ არსებობს არც აბონენტური გადასახადი და არც წინასწარი პაკეტი: ღონისძიება აქტიურია მისი შექმნის მომენტიდან.',
            emphasize: ['უფასოა'],
          },
          {
            label: '4.2.',
            text: `პლატფორმის ტექნიკური და პროგრამული მომსახურებისთვის დაწესებულია საშუამავლო საკომისიო, რომელიც შეადგენს ვიშლისტის მეშვეობით მიღებული თითოეული საჩუქრის ${TERMS_FEE}-ს. საკომისიო იკავება ღონისძიების საფულეში თანხის ასახვის მომენტში; სტუმარი იხდის ზუსტად იმ თანხას, რომელიც აირჩია, ხოლო ორგანიზატორის წმინდა ნაშთი შეადგენს საჩუქარს საკომისიოს გამოკლებით.`,
          },
          {
            label: '4.3.',
            text: 'სტუმრებს შეუძლიათ გადახდა საბანკო ბარათით, ლიცენზირებული საგადახდო პროვაიდერის მიერ მასპინძლობილი გადახდის გვერდის მეშვეობით. ბარათის მონაცემები შეიტანება უშუალოდ პროვაიდერის გვერდზე — კომპანია არ იღებს და არ ინახავს ბარათის სრულ მონაცემებს. იმ შემთხვევაში, როცა კომპანია საკუთარ დამატებით სერვისებზე სთავაზობს პირდაპირ საბანკო გადარიცხვას, ეს მეთოდი არ შეიცავს საბარათე დამუშავების საკომისიოს.',
          },
          {
            label: '4.4.',
            text: 'საჩუქრების ღირებულება შეიძლება განისაზღვროს ლარში (GEL), აშშ დოლარში (USD) ან ევროში (EUR). თანხა მიიღება, აღირიცხება და გაიცემა იმავე ვალუტაში, რომელშიც იგი ჩაირიცხა. კომპანია არ ახორციელებს ვალუტების კონვერტაციას და არ აჯამებს სხვადასხვა ვალუტის ბალანსებს; კონვერტაცია მომხმარებელსა და მის ბანკს შორის ურთიერთობის საგანია.',
            emphasize: ['არ ახორციელებს ვალუტების კონვერტაციას'],
          },
          {
            label: '4.5.',
            text: 'საქართველოს საგადასახადო კოდექსის შესაბამისად, კომპანიის (Storyfy.ge) ერთობლივ საგადასახადო შემოსავალს წარმოადგენს მხოლოდ 4.2 პუნქტით განსაზღვრული საკომისიო და მე-5 თავით გათვალისწინებულ დამატებით სერვისებზე დაწესებული საფასური.',
            emphasize: ['მხოლოდ 4.2 პუნქტით განსაზღვრული საკომისიო'],
          },
          {
            label: '4.6.',
            text: 'სტუმრების მიერ საიტის საშუალებით ჩარიცხული ძირითადი სასაჩუქრე თანხა (ასახული ბალანსის სრული მოცულობა მინუს პლატფორმის საკომისიო) არ წარმოადგენს პლატფორმის შემოსავალს ან საკუთრებას. შესაბამისად, აღნიშნული თანხა არ იბეგრება კომპანიის მხარეს.',
          },
          {
            label: '4.7.',
            text: 'ორგანიზატორი (იუბილარი) თავად არის პასუხისმგებელი ნებისმიერ საგადასახადო ვალდებულებაზე (ასეთის არსებობის შემთხვევაში), რომელიც შეიძლება წარმოეშვას მას ფიზიკური პირებისგან მიღებული საჩუქრების/გზავნილების შედეგად, მოქმედი კანონმდებლობის შესაბამისად.',
          },
          {
            label: '4.8.',
            text: 'სანამ ღონისძიება აქტიურია, მის მიერ მოზიდული თანხა აისახება ღონისძიების საფულეში. ორგანიზატორის მიერ ღონისძიების დახურვისას, წმინდა თანხა თითოეულ ვალუტაში გადადის მის ანგარიშის ბალანსზე და ფიქსირდება შესაბამისი ჩანაწერით, სადაც მითითებულია ეს ღონისძიება.',
          },
          {
            label: '4.9.',
            text: 'ანგარიშის ბალანსის ორგანიზატორის პირად საბანკო ანგარიშზე გადარიცხვა ხორციელდება მისი მოთხოვნის დაფიქსირებიდან არაუმეტეს 3 (სამი) სამუშაო დღის ვადაში. თანხა გაიცემა მხოლოდ ანგარიშის მფლობელის სახელზე გახსნილ საბანკო ანგარიშზე.',
            emphasize: ['არაუმეტეს 3 (სამი) სამუშაო დღის'],
          },
          {
            label: '4.10.',
            text: 'ანგარიშის ბალანსი წარმოადგენს კომპანიის მიერ მომხმარებლის წინაშე არსებული დავალიანების აღრიცხვას. იგი არ არის საბანკო დეპოზიტი, ელექტრონული ფული ან საინვესტიციო პროდუქტი და მასზე პროცენტი არ ერიცხება.',
          },
          {
            label: '4.11.',
            text: 'კომპანია უფლებამოსილია გადაავადოს თანხის გაცემა იმ გონივრული ვადით, რაც საჭიროა ანგარიშის მფლობელის იდენტიფიკაციის დასადასტურებლად, თაღლითობის ეჭვის შესამოწმებლად, ან ჩარჯბექისა თუ საჩუქართან დაკავშირებული დავის მოსაგვარებლად.',
          },
        ],
      },
      {
        title: '5. დამატებითი სერვისები და ფასები',
        intro:
          'ქვემოთ ჩამოთვლილ არასავალდებულო სერვისებს კომპანია სთავაზობს უშუალოდ. ისინი წარმოადგენს დანამატს უფასო ღონისძიებაზე და არასდროს არის პლატფორმით სარგებლობის წინაპირობა. დიზაინის ბიბლიოთეკა — მოწვევის ყველა შაბლონი, მოტივი, ფერი, ფონი, შრიფტების წყვილი და ცოცხალი გადახედვა — უფასოა; ერთადერთი ფასიანი დანამატი აქ ჩამოთვლილი ორია.',
        list: [
          {
            label: '5.1. ხელოვნური ინტელექტის კრედიტები:',
            text: `თითოეული ანგარიში იღებს ${AI_FREE_TRIES} უფასო გენერაციას. მათი ამოწურვის შემდეგ დამატებითი გენერაციები იძენება კრედიტების სახით, თითო გენერაციაზე ${AI_PRICE_GEL} ლარად. ტექნიკური მიზეზით ჩავარდნილი გენერაცია არ ხარჯავს არც უფასო ცდას და არც კრედიტს. კრედიტები მიბმულია ანგარიშზე, არ ექვემდებარება გადაცემას, არ აქვს ფულადი ღირებულება და არ ვადაგასულდება ანგარიშის აქტიურობის პერიოდში.`,
          },
          {
            label: '5.2. ღონისძიების ალბომის სივრცე:',
            text: `სივრცე იყიდება გიგაბაიტობით, ერთიანი ტარიფით — ${MEDIA_PRICE_LABEL} ლარი 1 GB-ზე, როგორც პირველი შეძენისას, ისე ყოველი შემდგომი შევსებისას. ღონისძიებაზე პირველი შეძენა ხსნის ${MEDIA_SERVICE_DAYS}-დღიან სამომსახურო ვადას. დამატებითი შევსება ზრდის ალბომის მოცულობას, მაგრამ არ ახანგრძლივებს ამ ვადას.`,
          },
          {
            label: '5.3. სამომსახურო ვადის დასრულება:',
            text: `${MEDIA_SERVICE_DAYS}-დღიანი ვადის ამოწურვისთანავე ალბომი იბლოკება ახალი ატვირთვებისთვის, თუმცა რჩება სანახავად და ჩამოსატვირთად ${MEDIA_GRACE_DAYS}-დღიანი საშეღავათო ვადით. საშეღავათო ვადის გასვლის შემდეგ ალბომში არსებული ყველა ფაილი სამუდამოდ იშლება საცავიდან. კომპანიას სხვა ასლი არ ინახება, ამიტომ ორგანიზატორმა ამ მომენტამდე უნდა ჩამოტვირთოს ყველაფერი, რისი შენახვაც სურს.`,
            emphasize: ['სამუდამოდ იშლება'],
          },
          {
            label: '5.4.',
            text: 'დამატებითი სერვისები არასავალდებულოა. მათი შეუძენლობა არანაირად არ ზღუდავს ღონისძიების უფასო ფუნქციონალს.',
          },
          {
            label: '5.5.',
            text: 'კომპანია უფლებამოსილია ნებისმიერ დროს შეცვალოს დამატებითი სერვისის ფასი, მხოლოდ მომავალი შენაძენებისთვის. ფასის ცვლილება არ ვრცელდება უკვე ანაზღაურებულ სერვისზე.',
          },
          {
            label: '5.6.',
            text: 'დამატებით სერვისებზე თანხის დაბრუნების წესები განსხვავდება საჩუქრებზე მოქმედი წესებისგან და მოცემულია მე-11 თავსა და ვებ-გვერდზე გამოქვეყნებულ თანხის დაბრუნების პოლიტიკაში.',
          },
        ],
      },
      {
        title: '6. ღონისძიების ალბომი, სტუმრების ატვირთვები და კონტენტის წესები',
        list: [
          {
            label: '6.1.',
            text: 'თუ ორგანიზატორი ჩართავს სტუმრების ატვირთვის ფუნქციას, ღონისძიების გასაზიარებელი ბმულის მფლობელ ნებისმიერ პირს შეეძლება ფოტოსა და ვიდეოს ატვირთვა რეგისტრაციის გარეშე. ფაილები სტუმრის ბრაუზერიდან პირდაპირ გადაეცემა კომპანიის საცავის პროვაიდერს მოკლევადიანი, ერთჯერადი დანიშნულების ბმულებით და არ გაივლის ვებ-გვერდის საკუთარ სერვერებს.',
          },
          {
            label: '6.2.',
            text: 'ატვირთვის ჩართვა-გამორთვა ორგანიზატორის კონტროლს ექვემდებარება. მას ნებისმიერ დროს შეუძლია გამორთოს სტუმრების ატვირთვა, დაალაგოს და გადაანაცვლოს ფაილები საქაღალდეებში, წაშალოს ცალკეული ფაილი ან ჩამოტვირთოს მთელი ალბომი.',
          },
          {
            label: '6.3.',
            text: 'მოქმედებს ტექნიკური ლიმიტები: არაუმეტეს 25 MB ერთ ფოტოზე და 4 GB ერთ ვიდეოზე, ასევე არაუმეტეს 500 ფაილი და ჯამში 50 GB ერთ ღონისძიებაზე. ღია ატვირთვის ბმულზე ბოროტად გამოყენების თავიდან ასაცილებლად ასევე მოქმედებს სიხშირის შეზღუდვები.',
          },
          {
            label: '6.4.',
            text: 'ალბომზე პასუხისმგებელია ორგანიზატორი: ღია ატვირთვის ბმულის გახსნის გადაწყვეტილებაზე, იმაზე, თუ ვის გაუზიარებს ამ ბმულს, და იმ კონტენტზე, რომელიც ალბომში აღმოჩნდება. კომპანია არ ახორციელებს ატვირთვების წინასწარ მოდერაციას.',
            emphasize: ['არ ახორციელებს ატვირთვების წინასწარ მოდერაციას'],
          },
          {
            label: '6.5.',
            text: 'აკრძალულია ისეთი კონტენტის ატვირთვა, რომელიც ეწინააღმდეგება კანონს, ასახავს პირს კერძო ან ინტიმურ გარემოში მისი თანხმობის გარეშე, სექსუალიზებს ან საფრთხეში აგდებს არასრულწლოვანს, ან არღვევს მესამე პირთა უფლებებს. ატვირთულ ფაილზე ასახულ ნებისმიერ პირს შეუძლია მოითხოვოს მისი წაშლა info@storyfy.ge-ზე მიმართვით.',
          },
          {
            label: '6.6.',
            text: 'ღონისძიების წაშლა სამუდამოდ შლის მის ალბომს — როგორც ფაილებს, ისე მათ ჩანაწერებს. ეს ქმედება შეუქცევადია და კომპანიას სარეზერვო ასლი არ ინახება. ორგანიზატორი ამის შესახებ გაფრთხილებულია წაშლის დადასტურებამდე.',
            emphasize: ['შეუქცევადია'],
          },
        ],
      },
      {
        title: '7. ხელოვნური ინტელექტის დიზაინერი',
        list: [
          {
            label: '7.1.',
            text: 'დიზაინერი ქმნის ფონურ გამოსახულებას, შრიფტების წყვილსა და ფერთა პალიტრას ორგანიზატორის მიერ მითითებული მონაცემების საფუძველზე — ეს არის ღონისძიების დასახელება, ტიპი, ენა და არასავალდებულო თავისუფალი ტექსტი. აღნიშნული მონაცემები დასამუშავებლად გადაეცემა ხელოვნური ინტელექტის მესამე მხარე პროვაიდერს. ორგანიზატორმა არ უნდა მიუთითოს ინფორმაცია, რომლის მესამე მხარის მიერ დამუშავებაც არ სურს.',
            emphasize: ['ხელოვნური ინტელექტის მესამე მხარე პროვაიდერს'],
          },
          {
            label: '7.2.',
            text: 'ხელოვნური ინტელექტის შედეგი გენერირდება ავტომატურად და მიწოდებულია არსებული სახით („as is“). კომპანია არ იძლევა გარანტიას შედეგის უნიკალურობაზე, სიზუსტეზე ან რომელიმე კონკრეტული მიზნისთვის ვარგისიანობაზე და არ იძლევა გარანტიას, რომ მსგავსი შედეგი არ დაუგენერირდება სხვა მომხმარებელს.',
          },
          {
            label: '7.3.',
            text: 'გარე ხელოვნური ინტელექტის პროვაიდერის მიუწვდომლობის შემთხვევაში პლატფორმა დიზაინს ქმნის საკუთარი ჩაშენებული გენერატორით. ასეთი დიზაინი ითვლება მიწოდებულ გენერაციად.',
          },
          {
            label: '7.4.',
            text: 'ორგანიზატორს უფლება აქვს გამოიყენოს დაგენერირებული დიზაინი პლატფორმაზე თავისი ღონისძიებისთვის და მისგან გაზიარებული მოწვევებისთვის. კომპანია ინახავს თითოეული გენერაციის ჩანაწერს აღრიცხვისა და ბოროტად გამოყენების პრევენციის მიზნით.',
          },
        ],
      },
      {
        title: '8. სარეფერალო პროგრამა',
        list: [
          {
            label: '8.1.',
            text: `თითოეულ რეგისტრირებულ მომხმარებელს აქვს პირადი სარეფერალო კოდი და ბმული. როდესაც ამ ბმულით რეგისტრირდება ახალი ორგანიზატორი და მისი ღონისძიება იღებს საჩუქრებს, რეფერალის ავტორი იღებს თითოეული ასეთი საჩუქრის ${TERMS_REFERRAL_FEE}-ს.`,
          },
          {
            label: '8.2.',
            text: `სარეფერალო ბონუსი გაიცემა კომპანიის ${TERMS_FEE}-იანი საკომისიოდან და არასდროს — ორგანიზატორის თანხიდან. მოწვეული ორგანიზატორი იღებს ზუსტად იმავე წმინდა თანხას, რასაც მიიღებდა რეფერალის გარეშე.`,
            emphasize: ['არასდროს — ორგანიზატორის თანხიდან'],
          },
          {
            label: '8.3.',
            text: 'ბონუსები ირიცხება რეფერალის ავტორის ანგარიშის ბალანსზე იმავე ვალუტაში, რომელშიც განხორციელდა საჩუქარი, და გაიცემა იმავე წესებით, რაც ვრცელდება ნებისმიერ სხვა ბალანსზე (მე-4 თავი).',
          },
          {
            label: '8.4.',
            text: 'აკრძალულია: საკუთარი თავის რეფერალად მითითება, ბონუსების მისაღებად დამატებითი ანგარიშების რეგისტრაცია, ბონუსების ხელოვნურად დაგროვების მიზნით ღონისძიების შექმნა ან საკუთარი საგადახდო ინსტრუმენტით საჩუქრის ჩარიცხვა, ასევე სარეფერალო ბმულის სპამით ან შეცდომაში შემყვანი ინფორმაციით გავრცელება. ამგვარად მიღებული ბონუსები ჩამოირთმევა, ხოლო ანგარიში შესაძლოა დაიბლოკოს.',
          },
          {
            label: '8.5.',
            text: 'კომპანია უფლებამოსილია ნებისმიერ დროს შეცვალოს ან შეწყვიტოს სარეფერალო განაკვეთი მომავლისთვის. უკვე დარიცხულ ბონუსებზე ეს ცვლილება არ ვრცელდება.',
          },
        ],
      },
      {
        title: '9. პასუხისმგებლობის შეზღუდვა და გარანტიები',
        list: [
          {
            label: '9.1.',
            text: 'კომპანია არ არის პასუხისმგებელი იუბილარის მიერ მიღებული ფულადი საჩუქრების შემდგომ მიზნობრივ ხარჯვაზე. პლატფორმა არ აკონტროლებს და არ იძლევა გარანტიას, რომ ორგანიზატორი რეალურად შეიძენს იმ ნივთს, რომელიც ვიშლისტში ჰქონდა მითითებული.',
          },
          {
            label: '9.2.',
            text: 'კომპანია არ აგებს პასუხს პლატფორმაზე რეგისტრირებული ღონისძიების შინაარსზე, ხარისხზე, მის გაუქმებაზე, გადადებაზე ან მასთან დაკავშირებულ ნებისმიერ ორგანიზაციულ ხარვეზზე.',
          },
          {
            label: '9.3.',
            text: 'ადმინისტრაცია არ არის პასუხისმგებელი საბანკო გადახდის სისტემების, ინტერნეტ-პროვაიდერების ან ეკვაირინგის მომსახურე ბანკის მხარეს წარმოქმნილ ტექნიკურ შეფერხებებზე, ტრანზაქციის დაგვიანებაზე ან უარყოფაზე.',
          },
          {
            label: '9.4.',
            text: 'მომხმარებელი (ორგანიზატორი) ვალდებულია უზრუნველყოს თავისი პროფილის უსაფრთხოება (პაროლი და ლოგინი). მესამე პირების მიერ პროფილზე არასანქცირებული წვდომით გამოწვეულ ზიანზე კომპანიას პასუხისმგებლობა არ ეკისრება.',
          },
          {
            label: '9.5.',
            text: 'პლატფორმა მიწოდებულია არსებული სახით („as is“) და ხელმისაწვდომობის შესაბამისად („as available“). კომპანია არ იძლევა გარანტიას უწყვეტ ან უშეცდომო მუშაობაზე და უფლებამოსილია ჩაატაროს ტექნიკური სამუშაოები, რომლებიც დროებით ზღუდავს წვდომას.',
          },
          {
            label: '9.6.',
            text: 'კომპანია არ არის პასუხისმგებელი სტუმრების მიერ ატვირთულ კონტენტზე, ასევე ალბომის ფაილების დაკარგვაზე, რაც დგება სამომსახურო ვადის ამოწურვის, საშეღავათო ვადის გასვლის ან ორგანიზატორის მიერ ღონისძიების წაშლის შედეგად, მე-5 და მე-6 თავებით გათვალისწინებული წესით.',
          },
          {
            label: '9.7.',
            text: 'მოქმედი კანონმდებლობით დაშვებულ ფარგლებში, დამატებით სერვისთან დაკავშირებით კომპანიის ჯამური პასუხისმგებლობა შემოიფარგლება იმ თანხით, რომელიც მომხმარებელმა ფაქტობრივად გადაიხადა ამ სერვისზე პრეტენზიის საფუძვლის წარმოშობამდე ბოლო 6 (ექვსი) თვის განმავლობაში. წინამდებარე დოკუმენტი არ ზღუდავს იმ პასუხისმგებლობას, რომლის შეზღუდვაც კანონით დაუშვებელია.',
          },
        ],
      },
      {
        title: '10. მომსახურების შეჩერება და შეწყვეტა',
        list: [
          {
            label: '10.1.',
            text: 'მომხმარებელს ნებისმიერ დროს შეუძლია შეწყვიტოს პლატფორმით სარგებლობა და მოითხოვოს ანგარიშის წაშლა მე-16 თავში მითითებულ საკონტაქტო მისამართზე მიმართვით.',
          },
          {
            label: '10.2.',
            text: 'კომპანია უფლებამოსილია შეაჩეროს ან შეწყვიტოს ანგარიში, დაბლოკოს ღონისძიება ან შეაჩეროს თანხის გაცემა, თუ არსებობს წინამდებარე წესების დარღვევის, თაღლითობის ან კანონდარღვევის დასაბუთებული ეჭვი.',
          },
          {
            label: '10.3.',
            text: 'მომსახურების შეწყვეტა არ აუქმებს კანონიერად დაგროვილ ანგარიშის ბალანსს. უდავო ნაშთი რჩება მომხმარებლისთვის გასაცემი, 4.11 პუნქტით გათვალისწინებული შემოწმების გათვალისწინებით.',
          },
          {
            label: '10.4.',
            text: 'ანგარიშის დახურვა წყვეტს ალბომის მოქმედ სამომსახურო ვადას; ფაილები იშლება 5.3 პუნქტის შესაბამისად, ხოლო გამოუყენებელი ხელოვნური ინტელექტის კრედიტები უქმდება.',
          },
        ],
      },
      {
        title: '11. თანხის დაბრუნების პოლიტიკა (Refund Policy)',
        intro:
          'საჩუქრებსა და დამატებით სერვისებზე ვრცელდება განსხვავებული წესები. სრული ტექსტი გამოქვეყნებულია ვებ-გვერდზე, ცალკე დოკუმენტის სახით; მოკლე შინაარსი შემდეგია.',
        list: [
          {
            label: '11.1. საჩუქრები:',
            text: 'სტუმრის მიერ ჩარიცხული სასაჩუქრე თანხის უკან დაბრუნება შესაძლებელია მხოლოდ იმ შემთხვევაში, თუ ღონისძიება ოფიციალურად გაუქმდა ორგანიზატორის მიერ და დაგროვილი თანხა ჯერ კიდევ არ არის გადარიცხული ორგანიზატორის პირად საბანკო ანგარიშზე.',
            emphasize: ['მხოლოდ'],
          },
          {
            label: '11.2.',
            text: 'მას შემდეგ, რაც თანხა გადაირიცხება ორგანიზატორის საბანკო ანგარიშზე, პლატფორმა სრულად იხსნის პასუხისმგებლობას ნებისმიერ ფინანსურ პრეტენზიაზე. სტუმარსა და ორგანიზატორს შორის წარმოშობილი ნებისმიერი დავა თანხის დაბრუნებასთან დაკავშირებით უნდა გადაწყდეს პლატფორმის გვერდის ავლით.',
          },
          {
            label: '11.3.',
            text: 'თანხის დაბრუნების შემთხვევაში, საბანკო ან საგადახდო დამუშავების საკომისიო (ასეთის არსებობის შემთხვევაში) შესაძლოა დაიქვითოს დასაბრუნებელი თანხიდან. საჩუქრის დაბრუნებისას მასზე დაკავებული საკომისიოც უბრუნდება გადამხდელს.',
          },
          {
            label: '11.4. დამატებითი სერვისები:',
            text: `გამოუყენებელი ხელოვნური ინტელექტის კრედიტები და გამოუყენებელი ალბომის სივრცე ექვემდებარება დაბრუნებას შეძენიდან ${ADDON_REFUND_DAYS} დღის განმავლობაში. უკვე მიწოდებული გენერაციები, ასევე ალბომის სივრცე, რომელშიც უკვე ატვირთულია ფაილები, დაბრუნებას არ ექვემდებარება, ვინაიდან მომსახურება შესრულებულია. ეს დათქმა არ ზღუდავს იმ უფლებებს, რომელთა უარყოფაც მომხმარებელთა უფლებების დაცვის კანონმდებლობით დაუშვებელია.`,
          },
        ],
      },
      {
        title: '12. ინტელექტუალური საკუთრება და საავტორო უფლებები',
        list: [
          {
            label: '12.1. პლატფორმის საკუთრება:',
            text: 'ვებ-გვერდზე Storyfy.ge განთავსებული ყველა მასალა, მათ შორის, მაგრამ არა მხოლოდ: პროგრამული კოდი, მონაცემთა ბაზები, ვებ-გვერდის დიზაინი, ინტერფეისი, გრაფიკული გამოსახულებები, ანიმაციები, სავაჭრო ნიშანი/ლოგო, ტექსტები და აუდიო-ვიზუალური კონტენტი წარმოადგენს კომპანიის ექსკლუზიურ ინტელექტუალურ საკუთრებას. მათი ნებისმიერი ფორმით კოპირება, რეპროდუქცია, გავრცელება, მოდიფიკაცია ან კომერციული მიზნებისთვის გამოყენება კომპანიის წინასწარი წერილობითი თანხმობის გარეშე მკაცრად აკრძალულია და ისჯება კანონით.',
          },
          {
            label: '12.2. მომხმარებლის მიერ ატვირთული კონტენტი:',
            text: 'ღონისძიების ორგანიზატორს უფლება აქვს პლატფორმაზე ატვირთოს ფოტო, ვიდეო, მუსიკა ან ტექსტური მასალა საკუთარი მოწვევის, ვიშლისტისა და ღონისძიების ალბომის პერსონალიზაციისთვის. მომხმარებელი აცხადებს და იძლევა გარანტიას, რომ ის არის ამ მასალების კანონიერი მფლობელი ან მოპოვებული აქვს შესაბამისი ლიცენზია/თანხმობა მათი გამოყენებისთვის. კონტენტზე საკუთრება რჩება მომხმარებელს, ხოლო კომპანიას ენიჭება მხოლოდ ის შეზღუდული ლიცენზია, რაც აუცილებელია სერვისის ფუნქციონირებისთვის მისი შენახვის, დამუშავებისა და ჩვენებისთვის.',
          },
          {
            label: '12.3. ხელოვნური ინტელექტით შექმნილი დიზაინები:',
            text: 'ხელოვნური ინტელექტის დიზაინერით შექმნილი დიზაინის გამოყენება ორგანიზატორს შეუძლია საკუთარი ღონისძიებისა და მისი მოწვევებისთვის. ვინაიდან ასეთი შედეგი ავტომატურად გენერირდება, კომპანია არ იძლევა განცხადებას იმის თაობაზე, რომ იგი დაცულია საავტორო უფლებით ან ექსკლუზიურია ორგანიზატორისთვის.',
          },
          {
            label: '12.4. პასუხისმგებლობის გამორიცხვა მესამე პირთა უფლებების დარღვევაზე:',
            text: 'კომპანია ფიზიკურად არ ახორციელებს მომხმარებლების მიერ ატვირთული კონტენტის წინასწარ მოდერაციას საავტორო უფლებებზე. მესამე პირთა ინტელექტუალური საკუთრების უფლებების დარღვევის შემთხვევაში, სრული სამართლებრივი და ფინანსური პასუხისმგებლობა ეკისრება უშუალოდ იმ მომხმარებელს, ვინც აღნიშნული მასალა ატვირთა პლატფორმაზე.',
          },
          {
            label: '12.5. საავტორო უფლებების დარღვევის შეტყობინება (Take-Down Notice):',
            text: 'თუ რომელიმე უფლებათმფლობელი მიიჩნევს, რომ Storyfy.ge-ზე განთავსებული რომელიმე კონტენტი არღვევს მის საავტორო უფლებებს, მას შეუძლია მოითხოვოს მასალის წაშლა საკონტაქტო ელ-ფოსტაზე (info@storyfy.ge) შეტყობინების გამოგზავნით. საფუძვლიანი მტკიცებულების არსებობის შემთხვევაში, კომპანია დაუყოვნებლივ წაშლის ან დაბლოკავს სადავო მასალას.',
          },
          {
            label: '12.6. მოწვევის შაბლონები და დიზაინის ბიბლიოთეკა:',
            text: 'დიზაინის ბიბლიოთეკაში არსებული შაბლონები, მოტივები, განლაგებები და სხვა გრაფიკული მასალა კომპანიის ან მისი ლიცენზიარების საკუთრებაა. ორგანიზატორი იღებს არაექსკლუზიურ, გადაუცემელ ლიცენზიას, გამოიყენოს შაბლონი საკუთარი ღონისძიების მოწვევისთვის პლატფორმაზე, ამ მოწვევის არსებობის ვადით. შაბლონის გრაფიკის ან დიზაინის ამოღება, პლატფორმის გარეთ რეპროდუქცია, გადაყიდვა ან საკუთარ ნამუშევრად წარდგენა დაუშვებელია. შრიფტები მიეწოდება Google Fonts-იდან მათი ღია ლიცენზიების შესაბამისად; ფონის სასაქონლო ფოტოებს გვაწვდის Unsplash, Unsplash-ის ლიცენზიით. ორგანიზატორის მიერ ფონად ატვირთული ფოტო რჩება ორგანიზატორის საკუთრებად 12.2 პუნქტის შესაბამისად.',
          },
        ],
      },
      {
        title: '13. აკრძალული ქმედებები',
        intro:
          'პლატფორმის გამოყენება მკაცრად აკრძალულია იმ ღონისძიებებისთვის ან მიზნებისთვის, რომლებიც:',
        list: [
          { text: 'ეწინააღმდეგება საქართველოს მოქმედ კანონმდებლობას;' },
          { text: 'ემსახურება ფულის გათეთრებას, თაღლითობას ან სხვა კრიმინალურ სქემებს;' },
          {
            text: 'მიზნად ისახავს არალეგალური ნივთიერებების, იარაღის ან აკრძალული სერვისების დაფინანსებას;',
          },
          { text: 'არღვევს მესამე პირთა საავტორო ან ინტელექტუალურ უფლებებს;' },
          {
            text: 'ბოროტად იყენებს სტუმრების ატვირთვის ღია ბმულს ან ღონისძიების ალბომს უკანონო მასალის ან პირის თანხმობის გარეშე გამოქვეყნებული გამოსახულებების გასავრცელებლად;',
          },
          {
            text: 'ბოროტად იყენებს ხელოვნური ინტელექტის დიზაინერს, მათ შორის უკანონო, სიძულვილის ენის შემცველი ან სექსუალური გამოსახულებების გენერირების ან გენერაციის ლიმიტების გვერდის ავლის მცდელობით;',
          },
          {
            text: 'მანიპულირებს სარეფერალო პროგრამით, მათ შორის საკუთარი თავის რეფერალად მითითებით, დუბლირებული ანგარიშებით ან საკუთარი სახსრებით ჩარიცხული საჩუქრებით;',
          },
          {
            text: 'ისახავს მიზნად Storyfy.ge-ის ღონისძიების ფარგლებში მოზიდულ საჩუქრებზე საკომისიოს არიდებას პლატფორმის გვერდის ავლით ანგარიშსწორებით, ან ცდილობს პლატფორმის ტექნიკურ ინფრასტრუქტურაში ჩარევას, მის სკანირებას ან გადატვირთვას.',
          },
        ],
        outro:
          'კომპანია იტოვებს უფლებას, ეჭვის გაჩენის შემთხვევაში, ცალმხრივად დაბლოკოს ნებისმიერი ღონისძიება ან ანგარიში, შეაჩეროს თანხის გაცემა და ინფორმაცია გადასცეს სამართალდამცავ ორგანოებს.',
      },
      {
        title: '14. ფორსმაჟორი',
        paragraph:
          'მხარეები თავისუფლდებიან პასუხისმგებლობისგან ვალდებულებების შეუსრულებლობისთვის, თუ ეს გამოწვეულია დაუძლეველი ძალის (ფორსმაჟორი) გარემოებებით, როგორიცაა: სტიქიური უბედურებები, ომი, საომარი მოქმედებები, სახელმწიფო რეგულაციები, მასშტაბური კიბერ-შეტევები ან გლობალური ინტერნეტ-კავშირის გათიშვა.',
      },
      {
        title: '15. ცვლილებები წესებსა და პირობებში',
        list: [
          {
            label: '15.1.',
            text: 'Storyfy.ge იტოვებს უფლებას, ნებისმიერ დროს, ცალმხრივად შეიტანოს ცვლილებები წინამდებარე დოკუმენტში. განახლებული ტექსტი ძალაში შედის საიტზე გამოქვეყნების მომენტიდან.',
          },
          {
            label: '15.2.',
            text: 'მომხმარებლის ვალდებულებაა პერიოდულად გადაამოწმოს მომსახურების წესები. პლატფორმით სარგებლობის გაგრძელება ცვლილებების გამოქვეყნების შემდეგ ნიშნავს მათ ავტომატურ აქცეპტს (თანხმობას).',
          },
          {
            label: '15.3.',
            text: 'საკომისიოს, სარეფერალო განაკვეთის ან დამატებითი სერვისის ფასის ცვლილება ძალაშია მხოლოდ მომავლისთვის. უკვე მიღებულ საჩუქრებზე, უკვე დარიცხულ ბონუსებსა და უკვე ანაზღაურებულ დამატებით სერვისებზე ვრცელდება იმ დროისთვის მოქმედი პირობები.',
          },
        ],
      },
      {
        title: '16. დავების გადაწყვეტა და საკონტაქტო ინფორმაცია',
        list: [
          {
            label: '16.1.',
            text: 'წინამდებარე ხელშეკრულებიდან გამომდინარე ნებისმიერი დავა წყდება მოლაპარაკების გზით. შეთანხმების მიუღწევლობის შემთხვევაში, დავა განიხილება საქართველოს სასამართლოების მიერ, საქართველოს მოქმედი კანონმდებლობის შესაბამისად.',
          },
        ],
        contact: {
          title: '16.2. საკონტაქტო ინფორმაცია:',
          emailLabel: 'ელ-ფოსტა:',
          websiteLabel: 'ვებ-გვერდი:',
        },
      },
    ],
  },
}
